import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useRef, useState } from 'react';

import { resolveApiMessage, showToast } from '../../helpers';
import {
  createOpsComment,
  deleteOpsComment,
  editOpsComment,
  getOpsComments,
  getOpsPeople,
  TOpsComment,
  TOpsPerson,
  TOpsRecordObject,
} from '../../requests/opsRecords';
import { useAppSelector } from '../../hooks/hooks';
import { btnGhost, btnPrimary, btnSecondary, Card, inputClass } from './ui';

/**
 * The per-record thread — mounted on a class, account, case or contact.
 * Newest first; the composer offers @-mention autocomplete against the
 * staff list, and a mention notifies the person in the sidebar bell.
 * Anyone can read; only the author edits or deletes their own comment.
 */

function fmtWhen(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Render @First Last / @first.last / @email as a highlighted token. */
function renderBody(body: string, people: TOpsPerson[]) {
  if (!people.length || !body.includes('@')) return body;
  const handles = people
    .flatMap((p) => {
      const local = p.email.split('@')[0];
      const out = [p.email, local];
      if (p.firstName && p.lastName) {
        out.push(
          `${p.firstName} ${p.lastName}`,
          `${p.firstName}.${p.lastName}`,
        );
      }
      return out;
    })
    .sort((a, b) => b.length - a.length)
    .map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const rx = new RegExp(`@(${handles.join('|')})(?![\\w@-])`, 'gi');
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = rx.exec(body))) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    parts.push(
      <span
        key={`${m.index}-${m[0]}`}
        className="rounded bg-blue-100 px-1 font-medium text-blue-primary"
      >
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts;
}

const Composer: React.FC<{
  people: TOpsPerson[];
  initial?: string;
  busy: boolean;
  submitLabel: string;
  onSubmit: (body: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}> = ({
  people,
  initial = '',
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  autoFocus,
}) => {
  const [body, setBody] = useState(initial);
  const [caret, setCaret] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);

  // The token being typed after the last "@" before the caret, if any.
  const token = useMemo(() => {
    const before = body.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at < 0) return null;
    if (at > 0 && /[\w]/.test(before[at - 1])) return null;
    const t = before.slice(at + 1);
    if (/[\n]/.test(t) || t.length > 40) return null;
    return { at, text: t };
  }, [body, caret]);

  const suggestions = useMemo(() => {
    if (!token) return [];
    const q = token.text.toLowerCase();
    return people
      .filter(
        (p) =>
          !q ||
          p.name.toLowerCase().includes(q) ||
          p.email.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [token, people]);

  const pick = (p: TOpsPerson) => {
    if (!token) return;
    const handle =
      p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.email;
    const next = `${body.slice(0, token.at)}@${handle} ${body.slice(caret)}`;
    setBody(next);
    const pos = token.at + handle.length + 2;
    setCaret(pos);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        autoFocus={autoFocus}
        className={`${inputClass} w-full`}
        rows={3}
        placeholder="Write a note for the team… use @ to mention someone"
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
        }}
        onClick={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && body.trim()) {
            e.preventDefault();
            onSubmit(body.trim());
            if (!initial) setBody('');
          }
        }}
      />
      {suggestions.length > 0 && (
        <div className="absolute left-0 z-20 mt-1 w-72 overflow-hidden rounded-lg border border-white-regular bg-white shadow-light">
          {suggestions.map((p) => (
            <button
              key={p.email}
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-blue-100/60"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(p)}
            >
              <span className="font-medium text-gray-four">{p.name}</span>
              <span className="ml-2 text-xs text-gray-bold">{p.email}</span>
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={busy || !body.trim()}
          onClick={() => {
            onSubmit(body.trim());
            if (!initial) setBody('');
          }}
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className={btnSecondary} onClick={onCancel}>
            Cancel
          </button>
        )}
        <span className="ml-auto text-xs text-gray-two">⌘/Ctrl + Enter</span>
      </div>
    </div>
  );
};

export const CommentThread: React.FC<{
  recordId: string;
  recordObject: TOpsRecordObject;
  title?: string;
  compact?: boolean;
}> = ({ recordId, recordObject, title = 'Comments', compact }) => {
  const queryClient = useQueryClient();
  const me = useAppSelector((s) => s.userReducer.user);
  const myEmail = (me?.email ?? '').toLowerCase();
  const [editing, setEditing] = useState<string | null>(null);

  const { data: people = [] } = useQuery({
    queryKey: ['opsPeople'],
    queryFn: getOpsPeople,
    staleTime: 5 * 60_000,
  });
  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['opsComments', recordId],
    queryFn: () => getOpsComments(recordId),
    enabled: Boolean(recordId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['opsComments', recordId] });
    queryClient.invalidateQueries({ queryKey: ['opsCommentCounts'] });
  };
  const create = useMutation({
    mutationFn: (body: string) =>
      createOpsComment({ recordId, recordObject, body }),
    onSuccess: (c: TOpsComment) => {
      showToast(
        c.mentions.length
          ? `Posted · ${c.mentions.length} ${
              c.mentions.length === 1 ? 'person' : 'people'
            } notified`
          : 'Posted',
        'success',
      );
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const edit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      editOpsComment(id, body),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteOpsComment(id),
    onSuccess: refresh,
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card title={`${title} · ${comments.length}`}>
      <div className={compact ? 'p-4' : 'p-5'}>
        <Composer
          people={people}
          busy={create.isPending}
          submitLabel="Post"
          onSubmit={(b) => create.mutate(b)}
        />
        <div className="mt-4 space-y-3">
          {isLoading ? (
            <div className="text-sm text-gray-two">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-sm text-gray-two">
              No comments yet — the first note on this record goes here.
            </div>
          ) : (
            comments.map((c) => {
              const mine = c.actorEmail.toLowerCase() === myEmail;
              return (
                <div
                  key={c.id}
                  className="rounded-lg border border-white-three px-3 py-2.5 text-sm"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-bold">
                    <span className="font-semibold text-gray-four">
                      {c.actorName ?? c.actorEmail}
                    </span>
                    <span>{fmtWhen(c.at)}</span>
                    {c.editedAt && <span>· edited</span>}
                    {mine && editing !== c.id && (
                      <span className="ml-auto flex gap-1">
                        <button
                          type="button"
                          className={`${btnGhost} px-1.5 py-0.5 text-xs`}
                          onClick={() => setEditing(c.id)}
                        >
                          edit
                        </button>
                        <button
                          type="button"
                          className={`${btnGhost} px-1.5 py-0.5 text-xs text-red-one hover:bg-red-100`}
                          onClick={() => {
                            if (window.confirm('Delete this comment?')) {
                              remove.mutate(c.id);
                            }
                          }}
                        >
                          delete
                        </button>
                      </span>
                    )}
                  </div>
                  {editing === c.id ? (
                    <div className="mt-2">
                      <Composer
                        people={people}
                        initial={c.body}
                        busy={edit.isPending}
                        submitLabel="Save"
                        autoFocus
                        onSubmit={(body) => edit.mutate({ id: c.id, body })}
                        onCancel={() => setEditing(null)}
                      />
                    </div>
                  ) : (
                    <div className="mt-1 whitespace-pre-wrap text-gray-five">
                      {renderBody(c.body, people)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Card>
  );
};

export default CommentThread;
